import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { ContentComposer } from '../../pages/strategist/ContentComposer';

/** Search params the composer owns; cleared together when the modal closes. */
// NB: the composer's tab param is `ctab`, NOT `tab` — `tab` is the Campaigns/Posts hub's Status
// filter, and the composer overlays the hub without a route change (shared search params).
const COMPOSER_PARAMS = ['compose', 'campaign', 'title', 'ctab'] as const;

/**
 * Site-wide Content Composer modal, driven entirely by the `?compose=` search
 * param so it can be opened from anywhere (New Content, Posts, Campaign detail, brief
 * themes) without navigating away from the current page.
 *
 *   - `compose=new`     → a fresh post.
 *   - `compose=<id>`    → edit that post.
 *   - `&campaign=<id>`  → preset (and lock) the campaign for a new post.
 *
 * Mounted once in the app shell (see WorkspaceGate). Renders nothing when the
 * param is absent.
 */
export function ComposerModal() {
  const [params, setParams] = useSearchParams();
  const panelRef = useRef<HTMLDivElement>(null);

  const compose = params.get('compose');
  const open = compose !== null && compose !== '';

  /** Switch destination by re-pointing `?compose=`, exactly as opening another post does. */
  const openSibling = useCallback(
    (siblingId: string) => {
      const next = new URLSearchParams(params);
      next.set('compose', siblingId);
      // The tab param belongs to the post being left; carrying it over would land the user on
      // Schedule for a destination they have not looked at yet.
      next.delete('ctab');
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const close = useCallback(() => {
    const next = new URLSearchParams(params);
    for (const key of COMPOSER_PARAMS) next.delete(key);
    setParams(next, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, close]);

  if (!open) return null;

  const editing = compose !== 'new';

  return (
    // Cover only the main content pane, not the sidebar (shown at lg+): the sidebar stays usable. The
    // offset is the sidebar's own 15rem, so the two still meet when index.css scales the UI to 80%.
    <div
      // Vertical padding intentionally omitted: the panel is exactly 95vh, so the remaining 5vh IS
      // the gutter. Adding py on top of that would push the panel past the viewport and make the
      // backdrop scroll.
      className="fixed inset-y-0 left-0 right-0 z-50 flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:left-[15rem]"
      role="dialog"
      aria-modal="true"
      aria-label={editing ? 'Edit post' : 'New post'}
    >
      <button
        type="button"
        aria-label="Close composer"
        className="absolute inset-0 cursor-default bg-black/40"
        onClick={close}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        // FIXED height, not just a cap: the modal must stay the same size as the user types, switches
        // tabs, or reveals a warning — otherwise it grows and shrinks under the cursor. The panel is
        // a flex column so the header stays put and only the body scrolls.
        className="relative z-10 flex h-[95vh] max-h-[95vh] w-full flex-col overflow-hidden rounded-2xl bg-card shadow-panel focus:outline-none"
      >
        <div className="flex shrink-0 items-center justify-between border-b border-line-soft bg-card px-6 py-4">
          <h2 className="text-base font-semibold text-ink">
            {editing ? 'Edit post' : 'New post'}
          </h2>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="focus-ring rounded-md p-1.5 text-ink-faint hover:bg-chip hover:text-ink-strong"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        </div>
        {/*
          The scroll container for everything below the header, and the sticky context a full-height
          side pane sticks to.

          `--composer-pane-h`, the height a side pane can occupy AND still be fully visible the
          moment it appears, is MEASURED against this element (`data-composer-scroll`) by
          useComposerPaneHeight, which the composer runs on the row its pane sits in: this
          scrollport, less whatever is drawn above that row - the test-mode banner, the destination
          strip, a notice, the tab strip, a row that wrapped - and less the composer's
          Save-and-close bar, which is pinned over this scrollport's bottom edge. It used to be a
          constant here (95vh - 12.25rem), true only for a bare post. Two facts that number rested
          on still hold:
            · Padding is on TOP only (pt-5), deliberately. A `sticky bottom-0` footer is constrained
              by its scroll container's PADDING box, not its border box, so any bottom padding here
              holds the composer's Save-and-close bar that far above the modal's edge and leaves a
              strip for content to scroll through, visibly, beneath it. Measured in Chrome: 20px of
              bottom padding = a 20px gap under the footer.
            · The pane must be SHORTER than the whole scrollport. Sized to all of it, its footer -
              the only buttons it has - is about 100px below the fold on open, and it is the tallest
              grid item, which leaves `sticky` no room to travel, so it never pins on a large display.
        */}
        <div data-composer-scroll className="flex-1 overflow-y-auto px-6 pt-5">
          {/*
            Keyed on the post, so switching destination REMOUNTS rather than re-pointing a live
            composer at a different row. That component hydrates once per post id and carries a ref
            to stop a re-render clobbering live edits; a clean mount is the state it is written for,
            and the alternative is trusting an ordering that has broken here before.
          */}
          {/* The destination strip lives INSIDE the composer now: only the composer knows the
              post's channel (the strip is hidden on Blog) and how to save an unsaved post, which
              is what adding a destination to a NEW post requires. */}
          {/* The test-mode banner is drawn by ContentComposer itself, as the first thing in its
              root: drawn here, above that root, it kept the composer's pinned bottom bar (and every
              way out) below the fold on a small phone. */}
          {/* One element around everything that scrolls: it changes height whenever anything
              above the pane's row does, which is what the measurement watches for. */}
          <div data-composer-content>
            <ContentComposer
              key={compose}
              id={editing ? compose : undefined}
              onClose={close}
              onSelectSibling={openSibling}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
